"use client"

import { Button, buttonVariants } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import { DateLib, DateRange, defaultDateLib, DropdownProps, WeekNumberProps } from "react-day-picker";
import { nlBE as locale } from "react-day-picker/locale";
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from "./ui/drawer";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";

export const WEEK_STARTS_ON = 1; // Monday

// Re-export types
export type { DateLib, DateRange };

export type ValidDateRange = { [P in keyof DateRange]-?: NonNullable<DateRange[P]>; };

export const isValidRange = (range: DateRange | undefined): range is ValidDateRange => {
  return !!range && !!range.from && !!range.to;
}

export const daysInRange = (range: DateRange | undefined): number => {
  return isValidRange(range) ? defaultDateLib.differenceInCalendarDays(range.to, range.from) + 1 : 0;
}

export const formatRange = (range: ValidDateRange): string => {
  const days = daysInRange(range);

  // Single day
  if (days === 1)
    return new Intl.DateTimeFormat(locale.code, {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(range.from)

  // Month (1st - last)
  if (range.from.getDate() === 1 && defaultDateLib.isSameDay(range.to, defaultDateLib.endOfMonth(range.from)))
    return `${new Intl.DateTimeFormat(locale.code, {
      month: "long",
      year: "numeric",
    }).format(range.from)}`

  // Week (Monday - Sunday)
  if (days === 7 && (range.from.getDay() === WEEK_STARTS_ON))
    return `week ${defaultDateLib.getWeek(range.from)}, ${new Intl.DateTimeFormat(locale.code, {
      day: "numeric",
      month: !defaultDateLib.isSameMonth(range.from, range.to) ? "short" : undefined,
      year: !defaultDateLib.isSameYear(range.from, range.to) ? "numeric" : undefined,
    }).format(range.from)} - ${new Intl.DateTimeFormat(locale.code, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(range.to)}`


  // Default
  return `${new Intl.DateTimeFormat(locale.code, {
    day: "numeric",
    month: !defaultDateLib.isSameMonth(range.from, range.to) ? "short" : undefined,
    year: !defaultDateLib.isSameYear(range.from, range.to) ? "numeric" : undefined,
  }).format(range.from)} - ${new Intl.DateTimeFormat(locale.code, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(range.to)}`
}

export interface TimeRangeSelectorProps {
  className?: string
  range: DateRange | undefined;
  setRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  maxRange?: DateRange;
  quickSelections?: (
    | { label: string; value: (dateLib: DateLib, range: DateRange | undefined, displayedMonth: Date | undefined) => DateRange }
    | { label: undefined }
  )[];
}

export const TimeRangeSelector = ({ className, range, setRange, maxRange, quickSelections }: TimeRangeSelectorProps) => {
  const [open, setOpen] = React.useState(false);
  const [displayedMonth, setDisplayedMonth] = React.useState<Date | undefined>(range?.from ?? undefined);
  const isMobile = useIsMobile()

  React.useEffect(() => {
    setDisplayedMonth(range?.from ?? undefined);
  }, [range]);

  const disableScroll = React.useCallback((dir: "next" | "prev") => {
    if (!isValidRange(range))
      return true;
    if (!isValidRange(maxRange))
      return false;

    const days = daysInRange(range);
    if (dir === "next") {
      const next = new Date(range.from);
      next.setDate(next.getDate() + days);
      next.setHours(0, 0, 0)
      return next > maxRange.to;
    } else {
      const prev = new Date(range.to);
      prev.setDate(prev.getDate() - days);
      prev.setHours(0, 0, 0)
      return prev < maxRange.from;
    }
  }, [range, maxRange]);

  const handleScroll = React.useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>, dir: "next" | "prev") => {
    const multiplier = dir === "next" ? 1 : -1;

    setRange((prev) => {
      if (!isValidRange(prev))
        return undefined;

      // Month
      if (defaultDateLib.isSameDay(prev.from, defaultDateLib.startOfMonth(prev.from)) && defaultDateLib.isSameDay(prev.to, defaultDateLib.endOfMonth(prev.to))) {
        if (multiplier > 0) {
          return {
            from: defaultDateLib.startOfMonth(defaultDateLib.addDays(prev.to, 1)),
            to: defaultDateLib.endOfMonth(defaultDateLib.addDays(prev.to, 1))
          };
        } else {
          return {
            from: defaultDateLib.startOfMonth(defaultDateLib.addDays(prev.from, -1)),
            to: defaultDateLib.endOfMonth(defaultDateLib.addDays(prev.from, -1))
          };
        }
      }

      // x days
      const days = (e.ctrlKey ? 1 : daysInRange(range)) * multiplier;
      return {
        from: new Date(prev.from.getFullYear(), prev.from.getMonth(), prev.from.getDate() + days),
        to: new Date(prev.to.getFullYear(), prev.to.getMonth(), prev.to.getDate() + days, 23, 59, 59)
      }
    });
  }, [range, setRange])

  const PopoverDrawer = isMobile ? Drawer : Popover;
  const PopoverDrawerTrigger = isMobile ? DrawerTrigger : PopoverTrigger;
  const PopoverDrawerContent = isMobile ? DrawerContent : PopoverContent;

  return (<div className={cn("flex flex-row gap-2 items-center", className)}>
    {!isMobile &&
      <Button variant="outline" className="p-0! aspect-square!" onClick={(e) => handleScroll(e, "prev")} disabled={disableScroll("prev")}>
        <ChevronLeft />
      </Button>
    }

    <PopoverDrawer open={open} onOpenChange={setOpen}>
      <PopoverDrawerTrigger asChild>
        <Button variant="outline" className={isMobile ? "" : "min-w-[250px]"}>
          <CalendarIcon className="size-4" />
          {!isMobile && <span className="text-sm text-muted-foreground">
            {isValidRange(range) ? formatRange(range) : "Kies datum"}
          </span>}
          <span className="sr-only">Select date</span>
        </Button>
      </PopoverDrawerTrigger>
      <PopoverDrawerContent className={cn("p-0 rounded-md border", isMobile ? "w-full" : "w-fit")} align="end">
        {isMobile &&
          <DrawerTitle className="p-4 flex flex-row items-center">
            <Button variant="outline" className="p-0! aspect-square!" onClick={(e) => handleScroll(e, "prev")} disabled={disableScroll("prev")}>
              <ChevronLeft />
            </Button>
            <span className="grow text-center ">
              {isValidRange(range) ? formatRange(range) : "Kies datum"}
            </span>
            <Button variant="outline" className="p-0! aspect-square!" onClick={(e) => handleScroll(e, "next")} disabled={disableScroll("next")}>
              <ChevronRight />
            </Button>
          </DrawerTitle>
        }
        <div className="flex flex-row">
          {!isMobile && quickSelections && (
            <div className="flex flex-col items-stretch p-2 border-r bg-sidebar rounded-l-md">
              {
                quickSelections.map((option, idx) => (
                  <React.Fragment key={idx}>
                    {option.label === undefined
                      ? <Separator className="my-2" />
                      : <Button variant="ghost" size="sm" onClick={() => {
                        setRange(option.value(defaultDateLib, range, displayedMonth));
                        setOpen(false)
                      }}>
                        {option.label}
                      </Button>
                    }
                  </React.Fragment>))
              }
            </div>
          )}

          <Calendar
            className="mx-auto"
            mode="range"
            locale={locale}
            selected={range}
            numberOfMonths={1}
            weekStartsOn={WEEK_STARTS_ON}
            showWeekNumber
            captionLayout="dropdown"
            month={displayedMonth}
            onMonthChange={setDisplayedMonth}
            modifiers={{
              unavailable: (date) => isValidRange(maxRange) ? date < maxRange.from || date > maxRange.to : false,
            }}
            modifiersClassNames={{
              unavailable: ""
            }}
            components={{
              Dropdown: (props) => <DropdownComponent {...props} />,
              WeekNumber: (props) => <WeekNumberComponent {...props} setRange={setRange} />,
            }}
            onSelect={(range) => {
              if (!isValidRange(range)) {
                return setRange(undefined);
              }

              setRange((prev) => {
                const maxRange = 2 * 31 * 24 * 60 * 60 * 1000; // 2 * 31 days in milliseconds
                if (range.to.getTime() - range.from.getTime() > maxRange) {
                  if (range.from !== prev?.from) {
                    range.to = new Date(range.from.getTime() + maxRange);
                  } else {
                    range.from = new Date(range.to.getTime() - maxRange);
                  }
                }
                return {
                  from: new Date(range.from.setHours(0, 0, 0)),
                  to: new Date(range.to.setHours(23, 59, 59))
                };
              })
            }}
          />
        </div>
      </PopoverDrawerContent>
    </PopoverDrawer>

    {!isMobile &&
      <Button variant="outline" className="p-0! aspect-square!" onClick={(e) => handleScroll(e, "next")} disabled={disableScroll("next")}>
        <ChevronRight />
      </Button>
    }
  </div>)
}

const DropdownComponent = ({ options, value, disabled, className, onChange }: DropdownProps) => {
  const isMonthDropdown = (className?.indexOf("month") ?? 0) > 0;
  return (
    <div className="z-[100]">
      <Select value={String(value)} onValueChange={(value) => {
        onChange?.({ target: { value } } as React.ChangeEvent<HTMLSelectElement>)
      }}>
        <SelectTrigger>
          <SelectValue placeholder="Select month" />
        </SelectTrigger>
        <SelectContent>
          <ScrollArea className={cn({ "h-80": (options?.length ?? 0) > 10 })}>
            {options?.map((option, id: number) => (
              <SelectItem key={`${option.value}-${id}`} value={String(option.value)} disabled={disabled}>
                {isMonthDropdown
                  ? new Intl.DateTimeFormat(locale.code, { month: "long" }).format(new Date(0, option.value))
                  : option.label
                }
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </Select>
    </div>
  )
}

const WeekNumberComponent = ({ children, week, className, setRange, ...props }: WeekNumberProps & Pick<TimeRangeSelectorProps, "setRange">) => {
  return (
    <td {...props}
      className={cn(buttonVariants({ variant: "ghost" }), "text-xs flex size-(--cell-size) items-center justify-center text-center mr-2 cursor-pointer", className)}
      onClick={() => setRange({
        from: week.days[0].date,
        to: new Date(week.days[6].date.setHours(23, 59, 59, 999))
      })}
    >
      <span className="-mt-1">
        {children}
      </span>
    </td>
  )
}